import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { Building2, KeyRound, Check } from "lucide-react";

export default function Onboarding() {
  const { user, refresh, logout } = useAuth();
  const { t, roleLabel } = useLang();
  const navigate = useNavigate();
  const [step, setStep] = useState(user?.tenant_id ? "role" : "tenant");
  const [choice, setChoice] = useState("create");
  const [companyName, setCompanyName] = useState("");
  const [inviteCode, setInviteCode] = useState("");
  const [presetRoles, setPresetRoles] = useState([]);
  const [role, setRole] = useState("");
  const [customRole, setCustomRole] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/roles").then(({ data }) => setPresetRoles(data.preset)).catch(() => {});
  }, []);

  const submitTenant = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (choice === "create") {
        await api.post("/tenants", { name: companyName });
      } else {
        await api.post("/tenants/join", { invite_code: inviteCode });
      }
      await refresh();
      setStep("role");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const submitRole = async () => {
    const finalRole = role === "__custom__" ? customRole.trim() : role;
    if (!finalRole) { toast.error(t("onb.selectRole")); return; }
    setBusy(true);
    try {
      await api.put("/users/me/role", { role: finalRole });
      await refresh();
      navigate("/uygulama");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="relative z-10 min-h-screen flex items-center justify-center p-6">
      <div className="w-full max-w-lg animate-fade-up">
        <div className="flex items-center justify-between mb-8">
          <span className="font-heading text-2xl font-black tracking-tighter text-primary">alignee</span>
          <div className="flex items-center gap-3">
            <LanguageSwitcher />
            <button onClick={logout} className="text-sm text-muted-foreground hover:text-foreground" data-testid="onboarding-logout-btn">{t("onb.logout")}</button>
          </div>
        </div>

        {step === "tenant" && (
          <div className="border border-border rounded-xl p-8 bg-card">
            <h1 className="font-heading text-2xl font-bold tracking-tight mb-1">{t("onb.wsTitle")}</h1>
            <p className="text-muted-foreground mb-6">{t("onb.wsSub")}</p>

            <div className="grid grid-cols-2 gap-3 mb-6">
              <button onClick={() => setChoice("create")} className={`text-left border rounded-lg p-4 transition-colors ${choice === "create" ? "border-primary bg-primary/5" : "border-border"}`} data-testid="choice-create">
                <Building2 className="w-5 h-5 text-primary mb-2" />
                <div className="font-semibold text-sm">{t("onb.create")}</div>
              </button>
              <button onClick={() => setChoice("join")} className={`text-left border rounded-lg p-4 transition-colors ${choice === "join" ? "border-primary bg-primary/5" : "border-border"}`} data-testid="choice-join">
                <KeyRound className="w-5 h-5 text-primary mb-2" />
                <div className="font-semibold text-sm">{t("onb.join")}</div>
              </button>
            </div>

            <form onSubmit={submitTenant} className="space-y-4">
              {choice === "create" ? (
                <div>
                  <Label htmlFor="company">{t("onb.companyName")}</Label>
                  <Input id="company" value={companyName} onChange={(e) => setCompanyName(e.target.value)} required placeholder={t("onb.companyPh")} className="mt-1.5 h-11" data-testid="company-name-input" />
                </div>
              ) : (
                <div>
                  <Label htmlFor="invite">{t("onb.inviteCode")}</Label>
                  <Input id="invite" value={inviteCode} onChange={(e) => setInviteCode(e.target.value.toUpperCase())} required placeholder="ABC12345" className="mt-1.5 h-11 font-mono tracking-widest" data-testid="invite-code-input" />
                </div>
              )}
              <Button type="submit" disabled={busy} className="w-full h-11 rounded-lg" data-testid="tenant-submit-btn">
                {busy ? t("onb.processing") : t("onb.continue")}
              </Button>
            </form>
          </div>
        )}

        {step === "role" && (
          <div className="border border-border rounded-xl p-8 bg-card">
            <h1 className="font-heading text-2xl font-bold tracking-tight mb-1">{t("onb.roleTitle")}</h1>
            <p className="text-muted-foreground mb-6">{t("onb.roleSub")}</p>

            <div className="grid grid-cols-2 gap-2 mb-4">
              {presetRoles.map((r) => (
                <button key={r} onClick={() => setRole(r)} className={`flex items-center justify-between text-left border rounded-lg px-4 py-3 text-sm transition-colors ${role === r ? "border-primary bg-primary/5 font-semibold" : "border-border hover:border-primary/40"}`} data-testid={`role-${r}`}>
                  {roleLabel(r)} {role === r && <Check className="w-4 h-4 text-primary" />}
                </button>
              ))}
            </div>
            <button onClick={() => setRole("__custom__")} className={`w-full text-left border rounded-lg px-4 py-3 text-sm transition-colors ${role === "__custom__" ? "border-primary bg-primary/5 font-semibold" : "border-border hover:border-primary/40"}`} data-testid="role-custom">
              {t("onb.customRole")}
            </button>
            {role === "__custom__" && (
              <Input value={customRole} onChange={(e) => setCustomRole(e.target.value)} placeholder={t("onb.customRolePh")} className="mt-3 h-11" data-testid="custom-role-input" />
            )}

            <Button onClick={submitRole} disabled={busy} className="w-full h-11 rounded-lg mt-6" data-testid="role-submit-btn">
              {busy ? t("onb.saving") : t("onb.enterWs")}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
